<template>
  <div :class="[{ 'cursor-not-allowed opacity-60': disabled }]" class="relative">
    <label v-if="label" class="text-txopacity text-sm font-medium">
      {{ label }}
      <span v-if="required" class="text-txopacity text-sm">*</span>
    </label>
    <div class="relative mt-1" :class="customWrapperClasses">
      <!-- Обычный input когда нет base -->
      <input
        v-if="!props.base"
        v-maska="getMask(props.mask ?? '')"
        :type="type || 'text'"
        :placeholder="placeholder"
        :rows="rows"
        class="placeholder:text-txopacity/30 text-txmain text-sm h-auto p-3 rounded-2xl ring-0 !border !border-border bg-white transition-all duration-300 disabled:bg-bgsidebar disabled:border-brgray"
        :class="[
          inputClasses,
          { 'cursor-not-allowed': disabled },
          { 'pr-10': slots.postfix },
          { 'pl-10': slots.prefix },
          { '': variant === 'light-primary' },
          { '!border-white': !isBorder },
        ]"
        :style="prefixText ? 'padding-left: ' + calculatedPadding + 'px' : ''"
        v-model="value"
        @input="handleInput"
        @focus="isFocused = true"
        @blur="handleBlur"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxlength"
      />

      <!-- Кастомный input с base -->
      <div
        v-else
        class="placeholder:text-txopacity/30 text-txmain text-sm h-auto p-3 rounded-2xl ring-0 !border !border-border bg-white transition-all duration-300 disabled:bg-bgsidebar disabled:border-brgray relative cursor-text"
        :class="[
          inputClasses,
          { 'cursor-not-allowed': disabled },
          { 'pr-10': slots.postfix },
          { '': variant === 'light-primary' },
          { '!border-white': !isBorder },
        ]"
        :style="{ '--prefix-width': calculatedPadding + 'px' }"
        @click="focusHiddenInput"
      >
        <span class="text-txsecondary">{{ props.base }}</span
        ><span :class="{ 'cursor-blink': isFocused }">{{ props.modelValue || "" }}</span>
        <input
          ref="hiddenInput"
          v-maska="getMask(props.mask ?? '')"
          :type="type || 'text'"
          class="absolute inset-0 opacity-0 cursor-text caret-black overflow-x-auto"
          :value="props.modelValue || ''"
          @input="handleInputBase"
          @focus="isFocused = true"
          @blur="handleBlur"
          :disabled="disabled"
          :readonly="readonly"
          :maxlength="maxlength"
          :style="{ caretColor: isFocused ? '#000' : 'transparent' }"
        />
      </div>

      <div
        v-if="prefixText && prefixText !== ''"
        class="absolute left-3 top-1/2 -translate-y-1/2 w-fit text-txsecondary"
        ref="prefixRef"
      >
        {{ prefixText }}
      </div>
      <div v-if="slots.prefix" class="absolute left-3 top-1/2 -translate-y-1/2">
        <slot name="prefix" />
      </div>
      <div v-if="slots.postfix" class="absolute right-3 top-1/2 -translate-y-1/2">
        <slot name="postfix" />
      </div>
    </div>
    <div v-if="example" class="text-txopacity text-xs mt-1 ml-3" :class="exampleClass">
      {{ example }}
    </div>
    <div v-if="errorMessage || validationError" class="text-red-500 text-xs mt-1 pl-3">
      {{ errorMessage || validationError }}
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { getMask } from "~/helpers/masks";
  interface ValidationRule {
    type:
      | "required"
      | "minLength"
      | "maxLength"
      | "digits"
      | "email"
      | "phone"
      | "url"
      | "slug"
      | "cyrillic"
      | "cyrillicPlus"
      | "cyrillicWithDigits"
      | "tglink"
      | "custom";
    value?: number | string;
    message: string;
    validator?: (value: string) => boolean;
  }

  interface Props {
    placeholder?: string;
    inputClasses?: string;
    modelValue?: string | number | undefined;
    required?: boolean;
    label?: string;
    errorMessage?: string;
    example?: string;
    exampleClass?: string;
    disabled?: boolean;
    mask?: string;
    type?: string;
    base?: string;
    variant?: string;
    maxlength?: number;
    rows?: number;
    isBorder?: boolean;
    readonly?: boolean;
    validationRules?: ValidationRule[];
    prefixText?: string;
    customWrapperClasses?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    placeholder: "Введите...",
    disabled: false,
    base: "",
    variant: "default",
    maxlength: undefined,
    rows: undefined,
    isBorder: true,
    readonly: false,
    customWrapperClasses: "",
    validationRules: () => [],
  });

  const emit = defineEmits(["update:modelValue", "update:validationError"]);

  const value = ref<string>(`${props.base}${props.modelValue}`);
  const validationError = ref<string>("");
  const isFocused = ref<boolean>(false);
  const baseWidth = ref<number>(0);
  const hiddenInput = ref<HTMLInputElement>();
  let validationTimeout: NodeJS.Timeout | null = null;

  // Функция для фокуса на скрытый input
  const focusHiddenInput = () => {
    if (hiddenInput.value && !props.disabled) {
      hiddenInput.value.focus();
    }
  };

  // Функция валидации
  const validateValue = (val: string): string => {
    if (!props.validationRules || props.validationRules.length === 0) {
      return "";
    }

    for (const rule of props.validationRules) {
      switch (rule.type) {
        case "required":
          if (!val || val.trim() === "") {
            return rule.message;
          }
          break;

        case "minLength":
          if (val.length < (rule.value as number)) {
            return rule.message;
          }
          break;

        case "maxLength":
          if (val.length > (rule.value as number)) {
            return rule.message;
          }
          break;

        case "digits":
          const digitCount = val.replace(/\D/g, "").length;
          if (digitCount !== (rule.value as number)) {
            return rule.message;
          }
          break;

        case "email":
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (val && !emailRegex.test(val)) {
            return rule.message;
          }
          break;

        case "phone":
          const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
          if (val && !phoneRegex.test(val)) {
            return rule.message;
          }
          break;

        case "url":
          // Валидация URL с протоколом или без
          const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
          if (val && !urlRegex.test(val)) {
            return rule.message;
          }
          break;

        case "slug":
          // Валидация slug: только латинские буквы, арабские цифры, дефис и подчеркивание
          const slugRegex = /^[a-zA-Z0-9_-]+$/;
          if (val && !slugRegex.test(val)) {
            return rule.message;
          }
          break;

        case "cyrillic":
          // Кириллические буквы, римские цифры (I, V, X, L, C, D, M), арабские цифры (0-9), пробелы, дефисы, апострофы, точки, запятые, тире
          // Не более одного разделителя подряд (кроме пробелов)
          const cyrillicRegex = /^[а-яёА-ЯЁIVXLCDM0-9\s\-'.,—]+$/;
          const multipleSeparatorsRegex = /[^а-яёА-ЯЁIVXLCDM0-9\s][^а-яёА-ЯЁIVXLCDM0-9\s]/;

          if (val && (!cyrillicRegex.test(val) || multipleSeparatorsRegex.test(val))) {
            return rule.message;
          }
          break;

        case "cyrillicPlus":
          // Кириллические буквы, римские цифры (I, V, X, L, C, D, M), арабские цифры (0-9), пробелы, дефисы, апострофы, точки, запятые, тире, кавычки, скобки
          // Не более одного разделителя подряд (кроме пробелов)
          const cyrillicPlusRegex = /^[а-яёА-ЯЁIVXLCDM0-9\s\-'.,—""«»()]+$/;
          const multipleSeparatorsRegexPlus = /[^а-яёА-ЯЁIVXLCDM0-9\s][^а-яёА-ЯЁIVXLCDM0-9\s]/;

          if (val && (!cyrillicPlusRegex.test(val) || multipleSeparatorsRegexPlus.test(val))) {
            return rule.message;
          }
          break;

        case "cyrillicWithDigits":
          // Кириллические буквы, римские цифры, арабские цифры, пробелы, дефисы, апострофы, точки, запятые, тире
          // Кавычки (« » ""), слэш (/), скобки (), номер (№), точка с запятой (;), двоеточие (:)
          // Поддержка сокращений с точками (УЛ., Д., КВ.) и тире в номерах
          // Не более одного разделителя подряд (кроме пробелов)
          const cyrillicWithDigitsRegex = /^[а-яёА-ЯЁIVXLCDM0-9\s\-'.,—«»""\/()№;:]+$/;
          const multipleSeparatorsRegex2 = /[^а-яёА-ЯЁIVXLCDM0-9\s№][^а-яёА-ЯЁIVXLCDM0-9\s№]/;

          if (val && !cyrillicWithDigitsRegex.test(val)) {
            return rule.message;
          }
          break;

        case "tglink":
          // Валидация ссылок для Telegram, VK и Max:
          // Telegram: @channel, t.me/channel, https://t.me/channel, http://t.me/channel, https://t.me/+channel, http://t.me/+channel, https://t.me/joinchat/channel, http://t.me/joinchat/channel
          // VK: @channel, vk.com/group, vk.ru/group, http(s)://vk.com/group, http(s)://vk.ru/group
          // Max: @channel, max.ru/channel, http(s)://max.ru/channel, http(s)://max.ru/join/<hash>
          const tglinkRegex =
            /^(@[a-zA-Z0-9_]+|(https?:\/\/)?(t\.me\/((\+[a-zA-Z0-9_]+)|(\+)|(joinchat\/[a-zA-Z0-9_-]+)|([a-zA-Z0-9_]+))|vk\.(com|ru)\/[a-zA-Z0-9_.\-]+|max\.ru\/(join\/[a-zA-Z0-9_\-]+|[a-zA-Z0-9_.\-]+)))$/;
          if (val && !tglinkRegex.test(val)) {
            return rule.message;
          }
          break;

        case "custom":
          if (rule.validator && !rule.validator(val)) {
            return rule.message;
          }
          break;
      }
    }

    return "";
  };

  const handleInput = (event: Event) => {
    const result = (event.target as HTMLInputElement).value;
    let processedValue = result;

    if (props.base) {
      const ost = result.replace(props.base, "");
      if (ost.length < 1 || !result.includes(props.base)) {
        value.value = props.base;
        validationError.value = validateValue("");
        emit("update:validationError", validationError.value);
        return;
      }
      processedValue = ost;
      emit("update:modelValue", ost);
    } else if (props.mask) {
      const numericValue = result.replace(/\D/g, "");
      processedValue = numericValue;
      emit("update:modelValue", numericValue);
    } else {
      emit("update:modelValue", result);
    }

    // Валидация с задержкой
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    validationTimeout = setTimeout(() => {
      // Для валидации используем только обработанное значение без base
      validationError.value = validateValue(processedValue);
      emit("update:validationError", validationError.value);
    }, 500);
  };

  const handleBlur = () => {
    isFocused.value = false;
    // Очищаем таймер при потере фокуса
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }
    // Валидация при потере фокуса
    let currentValue = value.value;
    if (props.base) {
      currentValue = currentValue.replace(props.base, "");
    } else if (props.mask) {
      currentValue = currentValue.replace(/\D/g, "");
    }
    validationError.value = validateValue(currentValue);
    emit("update:validationError", validationError.value);
  };

  watch(
    () => props.modelValue,
    (newVal) => {
      value.value = `${props.base}${newVal}`;
      // Валидация при программном изменении modelValue (только если не в фокусе)
      if (!isFocused.value) {
        if (validationTimeout) {
          clearTimeout(validationTimeout);
        }
        validationError.value = validateValue(String(newVal || ""));
        emit("update:validationError", validationError.value);
      }
    }
  );

  const handleInputBase = (event: Event) => {
    const result = (event.target as HTMLInputElement).value;
    let processedValue = result;

    if (props.mask) {
      const numericValue = result.replace(/\D/g, "");
      processedValue = numericValue;
      emit("update:modelValue", numericValue);
    } else {
      emit("update:modelValue", result);
    }

    // Валидация с задержкой
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    validationTimeout = setTimeout(() => {
      validationError.value = validateValue(processedValue);
      emit("update:validationError", validationError.value);
    }, 500);
  };

  // Очистка таймера при размонтировании
  onUnmounted(() => {
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }
  });

  const slots = useSlots();
  const prefixRef = ref<HTMLElement | null>(null);
  const prefixText = ref<string | null | undefined>(props.prefixText);
  const calculatedPadding = ref(40);
  const updatePadding = () => {
    // console.log("prefixText123", prefixText.value);
    nextTick(() => {
      if (prefixRef.value) {
        const width = prefixRef.value.offsetWidth;
        calculatedPadding.value = width + 12; // + отступы
      }
    });
  };

  watch(
    () => props,
    () => {
      // console.log(1, props.prefixText);
      if (!props.prefixText) {
        prefixText.value = null;
      } else {
        prefixText.value = props.prefixText;
      }
    },
    {
      deep: true,
    }
  );

  // Обновляем при изменении префикса
  watch(prefixText, updatePadding);

  onMounted(() => {
    updatePadding();
  });
</script>

<style scoped lang="scss">
  input {
    box-shadow: none !important;
    outline: none !important;

    &:focus {
      border-color: theme("colors.primary") !important;
    }
  }

  .cursor-blink {
    position: relative;

    &::after {
      content: "";
      display: inline-block;
      width: 1px;
      height: 1.2em;
      background-color: #000;
      margin-left: 1px;
      animation: blink 1s infinite;
      vertical-align: text-top;
    }
  }

  @keyframes blink {
    0%,
    50% {
      opacity: 1;
    }
    51%,
    100% {
      opacity: 0;
    }
  }
</style>
